import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface SupportTicket {
  id: string;
  ticketNumber: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  assignedTo: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user?: { id: string; firstName: string; lastName: string; email: string };
  assignee?: { id: string; firstName: string; lastName: string } | null;
  company?: { id: string; name: string } | null;
  replies?: { id: string; createdAt: string }[];
}

interface SupportTicketReply {
  id: string;
  message: string;
  isStaffReply: boolean;
  createdAt: string;
  user: { id: string; firstName: string; lastName: string; role: string };
}

interface TicketDetail extends SupportTicket {
  replies?: SupportTicketReply[];
}

interface TicketStats {
  open: number;
  inProgress: number;
  waitingOnSupport: number;
  waitingOnCustomer: number;
  resolved: number;
  closed: number;
}

interface SupportTicketManagerProps {
  onClose: () => void;
}

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting_on_customer', label: 'Waiting on Customer' },
  { value: 'waiting_on_support', label: 'Waiting on Support' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const STATUS_STYLES: Record<string, string> = {
  open: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  waiting_on_customer: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  waiting_on_support: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  closed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

const PRIORITY_STYLES: Record<string, string> = {
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

export const SupportTicketManager: React.FC<SupportTicketManagerProps> = ({ onClose }) => {
  const { token } = useAuth();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [replyMessage, setReplyMessage] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);

      const res = await fetch(`${API_BASE_URL}/api/support-tickets/admin/all?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTickets(data.tickets);
        setTotal(data.total);
      }
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [token, page, statusFilter, priorityFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/support-tickets/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [token]);

  const fetchTicketDetail = useCallback(async (ticketId: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/support-tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSelectedTicket(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch ticket detail:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [fetchTickets, fetchStats]);

  const handleStatusChange = async (ticketId: string, newStatus: string) => {
    setStatusUpdating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/support-tickets/admin/${ticketId}/status`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        fetchTickets();
        fetchStats();
        if (selectedTicket?.id === ticketId) {
          await fetchTicketDetail(ticketId);
        }
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    setReplySubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/support-tickets/${selectedTicket.id}/replies`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: replyMessage.trim() }),
      });
      if (res.ok) {
        setReplyMessage('');
        await fetchTicketDetail(selectedTicket.id);
        fetchTickets();
      }
    } catch (err) {
      console.error('Failed to send reply:', err);
    } finally {
      setReplySubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full h-5/6 m-4 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Support Tickets</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex">
          {/* Left panel - ticket list */}
          <div className={`${selectedTicket ? 'w-1/2 border-r border-gray-200 dark:border-gray-700' : 'w-full'} flex flex-col overflow-hidden`}>
            {/* Stats bar */}
            {stats && (
              <div className="flex gap-2 p-3 border-b border-gray-200 dark:border-gray-700 flex-wrap">
                <StatBadge label="Open" count={stats.open} color="blue" />
                <StatBadge label="In Progress" count={stats.inProgress} color="yellow" />
                <StatBadge label="Awaiting Support" count={stats.waitingOnSupport} color="purple" />
                <StatBadge label="Awaiting Customer" count={stats.waitingOnCustomer} color="orange" />
                <StatBadge label="Resolved" count={stats.resolved} color="green" />
              </div>
            )}

            {/* Filters */}
            <div className="flex gap-2 p-3 border-b border-gray-200 dark:border-gray-700">
              <select
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All Status</option>
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select
                value={priorityFilter}
                onChange={e => { setPriorityFilter(e.target.value); setPage(1); }}
                className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All Priority</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto self-center">{total} total</span>
            </div>

            {/* Ticket list */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading...</div>
              ) : tickets.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">No tickets found</div>
              ) : (
                <div>
                  {tickets.map(ticket => (
                    <div
                      key={ticket.id}
                      onClick={() => fetchTicketDetail(ticket.id)}
                      className={`p-3 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        selectedTicket?.id === ticket.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{ticket.ticketNumber}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${STATUS_STYLES[ticket.status] || ''}`}>
                              {STATUS_OPTIONS.find(s => s.value === ticket.status)?.label || ticket.status}
                            </span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${PRIORITY_STYLES[ticket.priority] || ''}`}>
                              {ticket.priority}
                            </span>
                          </div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{ticket.subject}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        {ticket.user && <span>{ticket.user.firstName} {ticket.user.lastName}</span>}
                        {ticket.company && <span>({ticket.company.name})</span>}
                        <span className="ml-auto">{formatDate(ticket.createdAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 p-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                >
                  Prev
                </button>
                <span className="text-xs text-gray-500 dark:text-gray-400">{page}/{totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* Right panel - ticket detail */}
          {selectedTicket && (
            <div className="w-1/2 flex flex-col overflow-hidden">
              {/* Detail header */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono text-gray-500 dark:text-gray-400">{selectedTicket.ticketNumber}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_STYLES[selectedTicket.priority] || ''}`}>
                      {selectedTicket.priority}
                    </span>
                  </div>
                  <button
                    onClick={() => { setSelectedTicket(null); setReplyMessage(''); }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm"
                  >
                    Close
                  </button>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{selectedTicket.subject}</h3>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-3">
                  {selectedTicket.user && (
                    <span>By: {selectedTicket.user.firstName} {selectedTicket.user.lastName} ({selectedTicket.user.email})</span>
                  )}
                  {selectedTicket.company && <span>| {selectedTicket.company.name}</span>}
                  <span>| {formatDate(selectedTicket.createdAt)}</span>
                </div>
                {/* Status change */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-600 dark:text-gray-400">Status:</label>
                  <select
                    value={selectedTicket.status}
                    onChange={e => handleStatusChange(selectedTicket.id, e.target.value)}
                    disabled={statusUpdating}
                    className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Conversation thread */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {/* Original description */}
                <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedTicket.user ? `${selectedTicket.user.firstName} ${selectedTicket.user.lastName}` : 'User'}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(selectedTicket.createdAt)}</span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>

                {/* Replies */}
                {selectedTicket.replies?.map(reply => (
                  <div
                    key={reply.id}
                    className={`border rounded-lg p-3 ${
                      reply.isStaffReply
                        ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800/30'
                        : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {reply.isStaffReply ? 'Support Staff' : `${reply.user.firstName} ${reply.user.lastName}`}
                      </span>
                      {reply.isStaffReply && (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded font-medium">Staff</span>
                      )}
                      <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(reply.createdAt)}</span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{reply.message}</p>
                  </div>
                ))}
              </div>

              {/* Reply form */}
              {selectedTicket.status !== 'closed' && (
                <div className="p-3 border-t border-gray-200 dark:border-gray-700">
                  <textarea
                    value={replyMessage}
                    onChange={e => setReplyMessage(e.target.value)}
                    placeholder="Reply as support staff..."
                    rows={3}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y mb-2"
                  />
                  <button
                    onClick={handleReply}
                    disabled={!replyMessage.trim() || replySubmitting}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {replySubmitting ? 'Sending...' : 'Reply as Staff'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatBadge: React.FC<{ label: string; count: number; color: string }> = ({ label, count, color }) => {
  const colorMap: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  };

  return (
    <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${colorMap[color] || colorMap.blue}`}>
      {label}: {count}
    </div>
  );
};

export default SupportTicketManager;
